import React, { useState, useEffect, useRef } from 'react';
import { 
  MessageSquare, 
  Send, 
  Users, 
  Circle,
  X,
  Minus,
  Maximize2
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import { cn, getInitials, formatTime } from '../lib/utils';
import { 
  getInternalChannels, 
  getInternalMessages, 
  sendInternalMessage,
  getTeamMembers 
} from '../lib/api';
import { toast } from 'sonner';

export const InternalChat = ({ isOpen, onToggle, currentUser, onNewMessage }) => {
  const [channels, setChannels] = useState([]);
  const [messages, setMessages] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      if (newMessage) {
        inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 120)}px`;
      }
    }
  }, [newMessage]);

  useEffect(() => {
    if (isOpen) {
      loadChannels();
      loadTeam();
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedChannel) {
      loadMessages();
    }
  }, [selectedChannel]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadChannels = async () => {
    try {
      const response = await getInternalChannels();
      setChannels(response.data || []);
      if (response.data?.length > 0 && !selectedChannel) {
        setSelectedChannel(response.data[0]);
      }
    } catch (error) {
      console.error('Error loading channels:', error);
    }
  };

  const loadMessages = async () => {
    if (!selectedChannel) return;
    setLoading(true);
    try {
      const response = await getInternalMessages(selectedChannel.id);
      setMessages(response.data || []);
      setUnreadCount(0);
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTeam = async () => {
    try {
      const response = await getTeamMembers();
      setTeamMembers(response.data || []);
    } catch (error) {
      console.error('Error loading team:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedChannel) return;

    try {
      await sendInternalMessage({
        channel_id: selectedChannel.id,
        content: newMessage.trim()
      });
      setNewMessage('');
      inputRef.current?.focus();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Erro ao enviar mensagem');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Handle incoming internal messages via WebSocket
  const addMessage = (message) => {
    if (message.channel_id === selectedChannel?.id) {
      setMessages(prev => [...prev, message]);
      if (minimized || !isOpen) {
        setUnreadCount(prev => prev + 1);
      }
    }
  };

  // Expose addMessage to parent
  useEffect(() => {
    if (onNewMessage) {
      onNewMessage(addMessage);
    }
  }, [selectedChannel, minimized, isOpen]);

  const onlineCount = teamMembers.filter(m => m.online).length;

  if (!isOpen) {
    return (
      <Button
        onClick={onToggle}
        className="fixed bottom-20 right-4 h-14 w-14 rounded-full shadow-lg z-50"
        size="icon"
      >
        <div className="relative">
          <Users size={24} />
          {unreadCount > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {unreadCount}
            </span>
          )}
        </div>
      </Button>
    );
  }

  return (
    <div 
      className={cn(
        "fixed bottom-20 right-4 bg-card border border-border rounded-lg shadow-xl z-50 flex flex-col transition-all",
        minimized ? "w-72 h-12" : "w-96 h-[500px]"
      )}
    >
      {/* Header */}
      <div 
        className="flex items-center justify-between p-3 border-b border-border cursor-pointer bg-secondary/30 rounded-t-lg"
        onClick={() => minimized && setMinimized(false)}
      >
        <div className="flex items-center gap-2">
          <Users size={18} className="text-primary" />
          <span className="font-medium text-sm">Chat da Equipe</span>
          <Badge variant="outline" className="text-xs">
            {onlineCount} online
          </Badge>
        </div>
        <div className="flex items-center gap-1">
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-7 w-7"
            onClick={(e) => { e.stopPropagation(); setMinimized(!minimized); }}
          >
            {minimized ? <Maximize2 size={14} /> : <Minus size={14} />}
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-7 w-7"
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
          >
            <X size={14} />
          </Button>
        </div>
      </div>

      {!minimized && (
        <>
          {/* Team Members Bar */}
          <div className="p-2 border-b border-border bg-secondary/20">
            <div className="flex gap-1 overflow-x-auto">
              {teamMembers.map((member) => (
                <div 
                  key={member.id}
                  className="flex flex-col items-center min-w-[50px]"
                  title={member.name}
                >
                  <div className="relative">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className={cn(
                        "text-xs",
                        member.role === 'admin' ? 'bg-primary text-primary-foreground' : 'bg-secondary'
                      )}>
                        {getInitials(member.name)}
                      </AvatarFallback>
                    </Avatar>
                    <Circle 
                      size={8} 
                      className={cn(
                        "absolute bottom-0 right-0 fill-current",
                        member.online ? "text-green-500" : "text-zinc-500"
                      )} 
                    />
                  </div>
                  <span className="text-[10px] text-muted-foreground truncate max-w-[50px] mt-1">
                    {member.name.split(' ')[0]}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Messages */}
          <ScrollArea className="flex-1 p-3">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                <MessageSquare className="w-10 h-10 mb-2 opacity-30" />
                <p className="text-sm">Nenhuma mensagem ainda</p>
                <p className="text-xs">Comece uma conversa com a equipe!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {messages.map((msg) => {
                  const isOwn = msg.sender_id === currentUser?.id;
                  return (
                    <div 
                      key={msg.id}
                      className={cn("flex gap-2", isOwn && "flex-row-reverse")}
                    >
                      {!isOwn && (
                        <Avatar className="h-7 w-7 shrink-0">
                          <AvatarFallback className={cn(
                            "text-xs",
                            msg.sender_role === 'admin' ? 'bg-primary text-primary-foreground' : 'bg-secondary'
                          )}>
                            {getInitials(msg.sender_name)}
                          </AvatarFallback>
                        </Avatar>
                      )}
                      <div className={cn(
                        "max-w-[75%] rounded-lg px-3 py-2",
                        isOwn 
                          ? "bg-primary text-primary-foreground" 
                          : "bg-secondary"
                      )}>
                        {!isOwn && (
                          <p className="text-xs font-medium mb-1 opacity-70">
                            {msg.sender_name}
                          </p>
                        )}
                        <p className="text-sm whitespace-pre-wrap break-words">
                          {msg.content}
                        </p>
                        <p className={cn(
                          "text-[10px] mt-1",
                          isOwn ? "text-primary-foreground/60" : "text-muted-foreground"
                        )}>
                          {formatTime(msg.timestamp)}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            )}
          </ScrollArea>

          {/* Input */}
          <div className="p-3 border-t border-border">
            <div className="flex items-end gap-2">
              <Textarea
                ref={inputRef}
                placeholder="Digite uma mensagem..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={1}
                className="flex-1 text-sm min-h-[40px] max-h-[120px] resize-none py-2 leading-relaxed"
              />
              <Button 
                size="icon" 
                onClick={handleSendMessage}
                disabled={!newMessage.trim()}
                className="mb-0.5"
              >
                <Send size={16} />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default InternalChat;
